import { fullTimestampFormatter } from "./traceUtils.js";

// A trace reading shows a message at one of three kinds of time, and the label
// says which. The record read returns the message's own time (`message_at`)
// with the source that supplied it (`message_time_source`) and the capture that
// carried it. A time the provider or a local transcript reported is when the
// message happened, and shows as a plain clock reading. A time Origin captured
// because the source gave none is marked "(captured)", as a captured response
// is. A message no source dated shows when Origin's backend first received it,
// marked "(received)": the message happened no later than that. A page from a
// server that predates those fields keeps the generic "recorded" stamp.
//
// The marker is baked into `label` inside this module so a render site cannot
// show a capture or receipt time as a bare clock reading by forgetting to add
// it. The title names the capture the time came from, and TraceStamp opens
// that capture, so an analyst can check a time against the event behind it.

export const STAMP_MESSAGE = "message";
export const STAMP_REPORTED = "reported";
export const STAMP_CAPTURED = "captured";
export const STAMP_RECEIVED = "received";
export const STAMP_RESPONSE = "response";
export const STAMP_TOOL = "tool";
export const STAMP_MIXED = "mixed";

const MARKER = {
  [STAMP_MESSAGE]: "",
  [STAMP_REPORTED]: "",
  [STAMP_CAPTURED]: " (captured)",
  [STAMP_RECEIVED]: " (received)",
  [STAMP_RESPONSE]: " (captured)",
  [STAMP_TOOL]: " (tool activity)",
  [STAMP_MIXED]: " (mixed records)",
};

const TITLE = {
  [STAMP_MESSAGE]: "Recorded message time. The source reports this instant where it can; where it does not, this is when Origin captured the message.",
  [STAMP_REPORTED]: "When the source says this message happened.",
  [STAMP_CAPTURED]: "When Origin captured this message. The source gave no time of its own.",
  [STAMP_RECEIVED]: "The source gave no time for this message. Origin first received it at this time, so it happened no later than this.",
  [STAMP_RESPONSE]: "When Origin captured this response on the endpoint. Not when the model emitted it.",
  [STAMP_TOOL]: "When this tool call was recorded in endpoint trace activity.",
  [STAMP_MIXED]: "This span's ends come from different kinds of record; read each end on its own item.",
};

// The two reported sources differ only in who reported the time.
const REPORTED_TITLE = {
  source_reported: "When the provider says this message happened.",
  transcript: "When the local transcript says this message happened.",
};

function stampAt(at, source, endAt = null, { title = TITLE[source], eventId = null, note = null } = {}) {
  const spans = Number.isFinite(endAt) && endAt !== at;
  return {
    at,
    endAt: spans ? endAt : null,
    iso: new Date(at).toISOString(),
    isoEnd: spans ? new Date(endAt).toISOString() : null,
    source,
    eventId,
    label: (spans ? fullTimestampFormatter.formatRange(at, endAt) : fullTimestampFormatter.format(at)) + MARKER[source],
    title: [title, eventId ? `From capture ${eventId}.` : null, note].filter(Boolean).join(" "),
  };
}

// A turn came from a capture if the conversation model built it from a
// manifest item (`sourceEventId`) or if it is the observed response, which
// carries its own observation's id under a different name. The synthetic
// "Tools offered" appendix has neither.
function fromCapture(turn) {
  return Boolean(turn?.sourceEventId || turn?.captured || turn?.observationSourceEventId);
}

// A message's own time, labeled by the source that supplied it. When Origin
// first received the message at a different time than that, the title says so,
// so a late upload or copied history shows on the stamp itself.
function messageStamp(turn) {
  const source = turn.messageTimeSource;
  const kind = source === "source_reported" || source === "transcript" ? STAMP_REPORTED
    : source === "capture_event" ? STAMP_CAPTURED
      : STAMP_MESSAGE;
  const received = Number.isFinite(turn.firstReceivedAt) ? fullTimestampFormatter.format(turn.firstReceivedAt) : null;
  return stampAt(turn.messageAt, kind, null, {
    title: REPORTED_TITLE[source] || TITLE[kind],
    eventId: turn.messageTimeEventId || null,
    note: received && received !== fullTimestampFormatter.format(turn.messageAt) ? `Origin first received it at ${received}.` : null,
  });
}

// The stamp for every turn on the axis, positionally aligned with `turns`.
//
// Three states, and the difference between the last two is the point:
//   undefined -- not a captured conversation item at all, so there is no
//                record to speak about and the reading shows nothing;
//   null      -- a real captured item with no time at all, so the reading
//                leaves the timestamp position empty;
//   a stamp   -- the message's own time, or when Origin first received it.
//
// Each turn is stamped from its own times. A time earlier than the turn above
// it still shows, because hiding it would hide evidence of the order things
// happened in. buildTurnTimes keeps its rule against backwards times for the
// event bucketing and the topic ribbon, which need a monotonic axis.
export function buildTurnStamps(turns = []) {
  return turns.map((turn) => {
    if (!fromCapture(turn)) return undefined;
    // Array position never establishes a turn's date; without an identity to
    // join on there is nothing to show, whatever instant rode along.
    if (!turn?.messageId) return null;
    if (turn.captured) return Number.isFinite(turn.messageAt) ? stampAt(turn.messageAt, STAMP_RESPONSE) : null;
    if (Number.isFinite(turn.messageAt)) return messageStamp(turn);
    if (Number.isFinite(turn.firstReceivedAt)) {
      return stampAt(turn.firstReceivedAt, STAMP_RECEIVED, null, { eventId: turn.firstReceivedEventId || null });
    }
    return null;
  });
}

// One stamp for the turns in [fromIndex, toIndex]. A span whose ends were
// recorded differently is described as mixed rather than taking one end's
// provenance for both. Undated turns inside the span neither contribute nor
// disqualify: the span is bounded by what was actually recorded in it.
export function rangeStamp(stamps = [], fromIndex = 0, toIndex = fromIndex) {
  const low = Math.max(0, Math.min(fromIndex, toIndex));
  const high = Math.min(stamps.length - 1, Math.max(fromIndex, toIndex));
  const dated = [];
  for (let index = low; index <= high; index += 1) {
    if (stamps[index]) dated.push(stamps[index]);
  }
  if (!dated.length) return null;
  // Stamps no longer rise turn by turn, so the span runs from its earliest
  // recorded instant to its latest rather than from its first turn to its last.
  const start = Math.min(...dated.map((stamp) => stamp.at));
  const end = Math.max(...dated.map((stamp) => stamp.endAt ?? stamp.at));
  const sources = new Set(dated.map((stamp) => stamp.source));
  return stampAt(start, sources.size === 1 ? dated[0].source : STAMP_MIXED, end);
}

export function toolStamp(at) {
  return Number.isFinite(at) ? stampAt(at, STAMP_TOOL) : null;
}

// Tool-call instants keyed by the provider's own tool_use id, which is the
// only identity a captured `tool_use` block and an endpoint-reported execution
// share.
export function toolStampsByUseId(executions = []) {
  const byUseId = new Map();
  for (const execution of executions) {
    const useId = execution?.toolUseId;
    const stamp = toolStamp(execution?.timestamp);
    if (!useId || !stamp) continue;
    byUseId.set(String(useId), stamp);
  }
  return byUseId;
}

// A block with no matching execution is `undefined`, never null: trace
// activity is read for the focal turn only and has a thirty-day horizon, so an
// unmatched call is one nobody asked about rather than one nobody recorded.
// Treating it as a known missing time would assert an absence never checked.
//
// Code-mode's virtual blocks carry client-invented ids (`<parent>#cm0`) that
// match no telemetry row; they are refused by their flag rather than by the
// shape of the id.
export function toolStampOf(byUseId, block) {
  if (!byUseId || !block || block.virtual || !block.id) return undefined;
  return byUseId.get(String(block.id)) ?? undefined;
}
